import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../types/redux';
import { 
  Keyboard, 
  BookOpen, 
  ChevronDown, 
  ChevronRight, 
  Share2, 
  Move, 
  MousePointer2, 
  Ruler, 
  Type, 
  Calculator,
  Copy
} from 'lucide-react';

type Tab = 'shortcuts' | 'tutorials';

export const HelpDialogContent: React.FC = () => {
  const preferences = useSelector((state: RootState) => state.preferences);
  const [activeTab, setActiveTab] = useState<Tab>('shortcuts');
  const [expandedTutorial, setExpandedTutorial] = useState<string | null>('double-line');

  const toggleTutorial = (id: string) => {
    setExpandedTutorial(expandedTutorial === id ? null : id);
  };

  const shortcuts = [
    {
      category: 'Tools',
      items: [
        { key: 'P', action: 'Pan Tool' },
        { key: 'J', action: 'Jump Indicators' },
        { key: 'M', action: 'Measurement Tool' },
        { key: 'A', action: 'Annotation Tool' },
        { key: 'E', action: 'Expression Tool' },
        { key: 'Esc', action: 'Return to Pan / Close Dialogs' },
      ]
    },
    {
      category: 'View & Zoom',
      items: [
        { key: 'Ctrl + =', action: 'Zoom In' },
        { key: 'Ctrl + -', action: 'Zoom Out' },
        { key: 'Ctrl + 0', action: 'Reset Zoom' },
      ]
    },
    {
      category: 'General',
      items: [
        { key: 'Ctrl + ,', action: 'Preferences' },
        { key: 'Ctrl + Alt + T', action: 'Toggle Theme' },
        { key: 'Ctrl + Alt + S', action: 'Share' },
        { key: 'Ctrl + Alt + L', action: 'Load' },
      ]
    }
  ];

  return (
    <div className="h-[60vh] flex flex-col -mx-6 -my-4">
      {/* Tabs */}
      <div className={`flex border-b ${preferences.theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
        <button
          onClick={() => setActiveTab('shortcuts')}
          className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
            activeTab === 'shortcuts'
              ? preferences.theme === 'dark'
                ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-800'
                : 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
              : preferences.theme === 'dark'
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Keyboard className="w-4 h-4" />
          Keyboard Shortcuts
        </button>
        <button
          onClick={() => setActiveTab('tutorials')}
          className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
            activeTab === 'tutorials'
              ? preferences.theme === 'dark'
                ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-800'
                : 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
              : preferences.theme === 'dark'
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          <BookOpen className="w-4 h-4" />
          Tutorials
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'shortcuts' ? (
          <div className="grid gap-6">
            {shortcuts.map((section) => (
              <div key={section.category}>
                <h3 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${
                  preferences.theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  {section.category}
                </h3>
                <div className={`rounded-lg border overflow-hidden ${
                  preferences.theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                  {section.items.map((item, index) => (
                    <div 
                      key={index}
                      className={`flex items-center justify-between px-4 py-3 ${
                        index !== section.items.length - 1
                          ? preferences.theme === 'dark' ? 'border-b border-gray-700' : 'border-b border-gray-200'
                          : ''
                      } ${
                        preferences.theme === 'dark' ? 'bg-gray-800/50' : 'bg-gray-50'
                      }`}
                    >
                      <span className={`text-sm ${preferences.theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                        {item.action}
                      </span>
                      <kbd className={`px-2 py-1 text-xs font-mono rounded border shadow-sm ${
                        preferences.theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-gray-300'
                          : 'bg-white border-gray-200 text-gray-600'
                      }`}>
                        {item.key}
                      </kbd>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Double Number Line Tutorial */}
            <TutorialSection
              id="double-line"
              title="Using Double Number Lines"
              expanded={expandedTutorial === 'double-line'}
              onToggle={() => toggleTutorial('double-line')}
              icon={<Copy className="w-5 h-5" />}
              theme={preferences.theme}
            >
              <div className="space-y-4 text-sm">
                <p>
                  Double number lines are perfect for teaching ratio and proportion. To enable:
                </p>
                <ol className="list-decimal list-inside space-y-2 ml-2">
                  <li>Click the <span className="font-bold text-green-600">Enable Secondary Line</span> button in the top toolbar (or use the "Double" dropdown).</li>
                  <li>Use the <span className="font-bold">Scale Factor</span> controls to adjust the relationship between the lines.</li>
                  <li>Click the + / - buttons to change the numerator or denominator independently.</li>
                </ol>
                <div className={`p-3 rounded border ${
                  preferences.theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'
                }`}>
                  <p className="font-medium mb-1">Example: 2:3 Ratio</p>
                  <p className="opacity-80">Set the scale factor to 1.5 (3/2). The bottom line will move 1.5 units for every 1 unit on the top line.</p>
                </div>
              </div>
            </TutorialSection>

            {/* Equivalent Fractions Tutorial */}
            <TutorialSection
              id="equiv-fractions"
              title="Equivalent Fractions Mode"
              expanded={expandedTutorial === 'equiv-fractions'}
              onToggle={() => toggleTutorial('equiv-fractions')}
              icon={<Calculator className="w-5 h-5" />}
              theme={preferences.theme}
            >
              <div className="space-y-4 text-sm">
                <p>
                  Visualize how different fractions align on the number line.
                </p>
                <ol className="list-decimal list-inside space-y-2 ml-2">
                  <li>Open the global toolbar on the left side of the screen.</li>
                  <li>Look for the <span className="font-bold">View Mode</span> section.</li>
                  <li>Select <span className="font-bold">Equivalent fractions</span>.</li>
                </ol>
                <p>
                  This creates multiple stacked number lines. You can configure each line's denominator to show how 1/2, 2/4, and 3/6 align perfectly!
                </p>
              </div>
            </TutorialSection>

            {/* Tools Tutorial */}
            <TutorialSection
              id="tools"
              title="Tools Overview"
              expanded={expandedTutorial === 'tools'}
              onToggle={() => toggleTutorial('tools')}
              icon={<MousePointer2 className="w-5 h-5" />}
              theme={preferences.theme}
            >
              <div className="grid grid-cols-1 gap-4 text-sm">
                <div className="flex gap-3">
                  <div className={`p-2 rounded-lg h-fit ${preferences.theme === 'dark' ? 'bg-gray-800' : 'bg-blue-50'}`}>
                    <Move className="w-4 h-4 text-blue-500" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Pan (P)</h4>
                    <p className="opacity-80">Click and drag to move the number line. Use mouse wheel to zoom.</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className={`p-2 rounded-lg h-fit ${preferences.theme === 'dark' ? 'bg-gray-800' : 'bg-blue-50'}`}>
                    <Share2 className="w-4 h-4 text-blue-500" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Jump Indicators (J)</h4>
                    <p className="opacity-80">Click and drag to create arcs showing addition/subtraction. Click label to edit value.</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className={`p-2 rounded-lg h-fit ${preferences.theme === 'dark' ? 'bg-gray-800' : 'bg-blue-50'}`}>
                    <Ruler className="w-4 h-4 text-blue-500" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Measurement (M)</h4>
                    <p className="opacity-80">Measure distance between two points. Measurements update automatically when scaled.</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className={`p-2 rounded-lg h-fit ${preferences.theme === 'dark' ? 'bg-gray-800' : 'bg-blue-50'}`}>
                    <Type className="w-4 h-4 text-blue-500" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Annotation (A)</h4>
                    <p className="opacity-80">Freehand draw or add text labels anywhere on the canvas.</p>
                  </div>
                </div>
              </div>
            </TutorialSection>

            {/* Sharing Tutorial */}
            <TutorialSection
              id="sharing"
              title="Sharing & Loading"
              expanded={expandedTutorial === 'sharing'}
              onToggle={() => toggleTutorial('sharing')}
              icon={<Share2 className="w-5 h-5" />}
              theme={preferences.theme}
            >
              <div className="space-y-4 text-sm">
                <p>
                  You can save your exact scene configuration to share with students or colleagues.
                </p>
                <div className="grid gap-4">
                  <div>
                    <h4 className="font-bold mb-1">To Share:</h4>
                    <p className="opacity-80">Click the <span className="font-bold">File</span> button and select <span className="font-bold">Share</span>. This generates a unique URL that you can copy and send.</p>
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">To Load:</h4>
                    <p className="opacity-80">Simply paste the URL into your browser. Alternatively, use <span className="font-bold">File {'>'} Load</span> to paste a URL manually.</p>
                  </div>
                </div>
              </div>
            </TutorialSection>
          </div>
        )}
      </div>
    </div>
  );
};

interface TutorialSectionProps {
  id: string;
  title: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  icon: React.ReactNode;
  theme: 'light' | 'dark';
}

const TutorialSection: React.FC<TutorialSectionProps> = ({ 
  title, 
  expanded, 
  onToggle, 
  children, 
  icon,
  theme 
}) => (
  <div className={`border rounded-lg overflow-hidden transition-colors ${
    theme === 'dark' ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-white'
  }`}>
    <button
      onClick={onToggle}
      className={`w-full px-4 py-3 flex items-center justify-between text-left transition-colors ${
        theme === 'dark' ? 'hover:bg-gray-800' : 'hover:bg-gray-50'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={theme === 'dark' ? 'text-blue-400' : 'text-blue-500'}>
          {icon}
        </div>
        <span className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
          {title}
        </span>
      </div>
      {expanded ? (
        <ChevronDown className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
      ) : (
        <ChevronRight className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
      )}
    </button>
    {expanded && (
      <div className={`px-4 py-4 border-t ${
        theme === 'dark' 
          ? 'border-gray-700 text-gray-300 bg-gray-900/20' 
          : 'border-gray-100 text-gray-600 bg-gray-50/50'
      }`}>
        {children}
      </div>
    )}
  </div>
);

export default HelpDialogContent;
